/**
 * Load marker icons from map-styles service for symbol layers
 * Icons are loaded as SVG files from the sprites endpoint
 */

import brandConfig from '../config/brandConfig';

// Icon definitions - files served from map-styles/sprites/
const MARKER_ICONS = [
  { name: 'tree-icon', file: 'tree.svg' },
  { name: 'entrance-icon', file: 'entrance.svg' },
  { name: 'exit-icon', file: 'exit.svg' },
  { name: 'parking-icon', file: 'parking.svg' },
  { name: 'camera-icon', file: 'camera.svg' },
  { name: 'poi-icon', file: 'poi.svg' },
];

/**
 * Load SVG from URL and convert to Mapbox image data
 * @param {string} url - URL of the SVG file
 * @param {number} size - Output size in pixels
 * @returns {Promise<{width: number, height: number, data: Uint8ClampedArray}>}
 */
const loadSvgAsImageData = (url, size = 64) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d', { alpha: true });
      // Ensure fully transparent background before drawing
      ctx.clearRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);

      try {
        const imageData = ctx.getImageData(0, 0, size, size);
        resolve({
          width: size,
          height: size,
          data: imageData.data
        });
      } catch (err) {
        reject(new Error(`Canvas error: ${err.message}`));
      }
    };

    img.onerror = () => {
      reject(new Error(`Failed to load image from ${url}`));
    };

    img.src = url;
  });
};

/**
 * Load a single icon and add it to the map
 */
const loadIcon = async (map, iconName, url) => {
  // Skip if already loaded (e.g., from sprite sheet)
  if (map.hasImage(iconName)) {
    return;
  }

  try {
    const imageData = await loadSvgAsImageData(url, 64);
    // Add with sdf: false to ensure original colors and transparency are preserved
    map.addImage(iconName, imageData, { sdf: false });
  } catch (err) {
    console.warn(`[MarkerIcons] Failed to load ${iconName}:`, err.message);
  }
};

/**
 * Load all marker icons into the map from the sprites service
 * @param {Object} map - Mapbox GL map instance
 * @returns {Promise}
 */
export const loadMarkerIcons = async (map) => {
  if (!map) {
    console.error('[MarkerIcons] Map instance is required');
    return;
  }

  const spritesUrl = brandConfig.spritesUrl;

  // Load all icons in parallel
  const loadPromises = MARKER_ICONS.map(({ name, file }) => {
    const url = `${spritesUrl}/${file}`;
    return loadIcon(map, name, url);
  });

  try {
    await Promise.all(loadPromises);

    // Trigger repaint to ensure symbol layers render with new icons
    if (map.triggerRepaint) {
      map.triggerRepaint();
    }
  } catch (error) {
    console.error('[MarkerIcons] Error:', error);
  }
};

export default loadMarkerIcons;
